import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    Button,
    TextField,
    Switch,
    Snackbar,
    Alert,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { PageContainer, formatDate } from 'components/shared';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';

const InHouseShop = () => {
  const [temporaryClose, setTemporaryClose] = useState(false);
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(500);
  const [freeDeliveryOverAmount, setFreeDeliveryOverAmount] = useState(1000);
  const [toast, setToast] = useState(null);

  const { data: settingsResp } = useGetSettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateSettingsMutation();

  const createdAt = settingsResp?.data?.createdAt;

  useEffect(() => {
    const saved = settingsResp?.data?.custom?.inHouseShop;
    if (saved) {
      if (saved.temporaryClose != null) setTemporaryClose(saved.temporaryClose);
      if (saved.minimumOrderAmount != null) setMinimumOrderAmount(saved.minimumOrderAmount);
      if (saved.freeDeliveryOverAmount != null) setFreeDeliveryOverAmount(saved.freeDeliveryOverAmount);
    }
  }, [settingsResp]);

  const handleSaveInfo = async () => {
    try {
      await updateSettings({
        custom: {
          inHouseShop: {
            temporaryClose,
            minimumOrderAmount: Number(minimumOrderAmount) || 0,
            freeDeliveryOverAmount: Number(freeDeliveryOverAmount) || 0,
          },
        },
      }).unwrap();
      setToast({ severity: 'success', msg: 'Shop settings saved' });
    } catch (err) {
      setToast({ severity: 'error', msg: err?.data?.error?.message || 'Failed to save' });
    }
  };

  const handleTemporaryCloseChange = (event) => setTemporaryClose(event.target.checked);
  const handleMinimumOrderAmountChange = (event) => setMinimumOrderAmount(event.target.value);
  const handleFreeDeliveryOverAmountChange = (event) => setFreeDeliveryOverAmount(event.target.value);

  return (
    <PageContainer
      title="In-House Shop"
      subtitle="Manage your in-house shop business setup and order preferences."
    >
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">
          <ShoppingCartIcon sx={{ mr: 1 }} /> Business Setup
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body1">Temporary Close</Typography>
        <Switch checked={temporaryClose} onChange={handleTemporaryCloseChange} />
        <Typography variant="caption">
          By turning on the "Temporary Close" button, admin can pause shop activities. Customers will not be able to order or purchase from the shop.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Shop Details</Typography>
        <Typography variant="body2" color="text.secondary">
          Created at: {formatDate(createdAt)}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Shop Settings</Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Minimum Order Amount (₹)"
          type="number"
          value={minimumOrderAmount}
          onChange={handleMinimumOrderAmountChange}
          InputProps={{ inputProps: { min: 0 } }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Free Delivery Over Amount (₹)"
          type="number"
          value={freeDeliveryOverAmount}
          onChange={handleFreeDeliveryOverAmountChange}
          InputProps={{ inputProps: { min: 0 } }}
        />
      </Grid>

      {/* Save Button - Right Aligned & 4 Grid Wide */}
      <Grid item xs={12} md={4} sx={{ ml: 'auto', textAlign: 'right' }}>
        <Button variant="contained" color="primary" fullWidth onClick={handleSaveInfo} disabled={saving}>
          {saving ? 'Saving…' : 'Save Information'}
        </Button>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : null}
      </Snackbar>
    </Grid>
    </PageContainer>
  );
};

export default InHouseShop;
